import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, fileUrl } from "@/lib/api";
import { FanPageView } from "@/components/fanpage/FanPageView";
import {
  initMetaPixels,
  trackPixelEvent,
  trackStandardPixelEvent,
  trackSinglePixelCustomEvent,
  ensurePixelInitialized,
} from "@/lib/metaPixel";
import { detectTrafficSource } from "@/lib/trafficSource";
import { setPageMeta } from "@/lib/seo";

// Fixed custom-conversion rule: any event page with a ticket CTA labeled exactly
// "Book Stalls" fires a "Book Stalls" custom event scoped only to the TT Insider pixel,
// regardless of which other pixels the page owner has configured.
const BOOK_STALLS_CTA_LABEL = "Book Stalls";
const BOOK_STALLS_PIXEL_ID = "833697471403110";

export default function PublicFanPage() {
  const { slug, handle } = useParams();
  const navigate = useNavigate();
  const [page, setPage] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const source = useRef(detectTrafficSource());

  useEffect(() => {
    const fetchAndTrack = () => {
      api
        .get(`/public/${slug}`, { params: { source: source.current } })
        .then(({ data }) => {
          setPage(data);
          if (data.meta_pixels?.length) {
            initMetaPixels(data.meta_pixels.map((p) => p.pixel_id));
          }
          const hasBookStallsCta = (data.tour_stops || []).some((stop) => stop.cta_label === BOOK_STALLS_CTA_LABEL);
          if (hasBookStallsCta) {
            ensurePixelInitialized(BOOK_STALLS_PIXEL_ID);
          }
          const pageTitle = data.type === "event" ? data.event_name : data.title;
          setPageMeta({
            title: pageTitle ? `${pageTitle} — SmartLinks` : "SmartLinks",
            description: data.description || "One smart link for every streaming platform and ticket stop.",
            image: data.og_image_url ? fileUrl(data.og_image_url) : data.cover_image_url ? fileUrl(data.cover_image_url) : undefined,
            url: window.location.href,
          });
        })
        .catch(() => setNotFound(true));
    };

    if (handle) {
      // Direct hit on the canonical /:handle/:slug URL — fetch and track normally.
      fetchAndTrack();
    } else {
      // Legacy /s/:slug link — resolve the canonical handle first (no view recorded here)
      // so we redirect once instead of double-counting a view on both URLs.
      api
        .get(`/public/${slug}/resolve`)
        .then(({ data }) => {
          if (data.url_handle) {
            navigate(`/${data.url_handle}/${slug}`, { replace: true });
          } else {
            fetchAndTrack();
          }
        })
        .catch(() => setNotFound(true));
    }
  }, [slug, handle, navigate]);

  const handleLinkClick = (linkId, linkLabel, ctaLabel) => {
    api.post(`/public/${slug}/click`, { link_id: linkId, link_label: linkLabel, source: source.current }).catch(() => {});
    if (page.type === "music") {
      trackStandardPixelEvent("AddToWishlist", { content_name: linkLabel });
    } else {
      trackPixelEvent("LinkClick", { link_label: linkLabel });
      if (ctaLabel === BOOK_STALLS_CTA_LABEL) {
        trackSinglePixelCustomEvent(BOOK_STALLS_PIXEL_ID, BOOK_STALLS_CTA_LABEL, { link_label: linkLabel });
      }
    }
  };

  if (notFound) {
    return (
      <div className="min-h-screen bg-[#0B0E14] flex items-center justify-center text-center px-6" data-testid="fan-page-not-found">
        <div className="space-y-2">
          <p className="text-5xl mb-2">🔗</p>
          <h1 className="text-xl font-bold text-white font-display">This link doesn't exist</h1>
          <p className="text-sm text-slate-400">It may be unpublished or the URL is incorrect.</p>
        </div>
      </div>
    );
  }

  if (!page) {
    return (
      <div className="min-h-screen bg-[#0B0E14] flex items-center justify-center" data-testid="fan-page-loading">
        <div className="h-8 w-8 rounded-full border-2 border-white/30 border-t-white animate-spin" />
      </div>
    );
  }

  return <FanPageView page={page} onLinkClick={handleLinkClick} scale="full" />;
}
